import React, { useState } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import { SALES_VAT, TOTAL_LABEL } from '../../../common/charts/chartDataConfig';
import Chart from '../../../common/charts/Chart';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { getLabels, getValues } from '../../../../utils/chart-utils';
import { isNumber } from '../../../../utils/report-utils';
import TopSalesReportForm from './TopSalesReportForm';
import CommonContainer from '../../../common/CommonContainer';
import RegularDataGrid from '../../../common/table/RegularDataGrid';
import DataGridCell from '../../../common/table/DataGridCell';
import PropTypes from 'prop-types';
import { clearStandardReports } from '../../../../actions/report-actions';
import { TOP_SALES_HEADERS } from './TopSalesReportConfig';
import { getHeaderTranslationForStandardReports } from '../standard/StandardReportUtils';
import {
  CATEGORY_COLUMN,
  NUMBER_OF_SOLD_GOODS_COLUMN,
  PRODUCT_COLUMN,
  SALES_INCL_VAT,
} from '../standard/StandardReportConsts';
import NoDataAvailable from '../../../common/charts/NoDataAvailable';
import CommonDivider from '../../../common/CommonDivider';
import { BACKGROUND_DARK, BACKGROUND_WHITE } from '../../../../const/color-const';
import { DownloadToolbar } from '../sie4/Sie4ReportConfig';
import { downloadTransactionFileForExcel } from '../../../../actions/sie4-actions';

const useStyles = makeStyles(theme => ({
  pageWrapper: {
    width: '100%',
    background: theme.palette.type === 'light' ? BACKGROUND_WHITE : BACKGROUND_DARK,
  },
  topSalesHeader: {
    alignSelf: 'center',
    paddingTop: theme.spacing(3),
  },
  chartWrapper: {
    margin: theme.spacing(8, 0, 8, 0),
    paddingTop: theme.spacing(4),
    width: '100%',
    maxWidth: '100%',
    [theme.breakpoints.up('md')]: {
      maxWidth: '70%',
    },
  },
}));

const DownloadToolbarWithProgress = ({
                                       reportName,
                                       formValues,
                                       machinesOptions,
                                       downloadTransactionFileForExcel,
                                     }) => {
  const [downloadProgress, setDownloadProgress] = useState(null);
  
  return (
    <DownloadToolbar
      reportName={reportName}
      formValues={formValues}
      machinesOptions={machinesOptions}
      setDownloadProgress={setDownloadProgress}
      downloadProgress={downloadProgress}
      downloadTransactionFileForExcel={downloadTransactionFileForExcel}
    />
  );
};

const TopSalesReport = ({
                          machines,
                          isCategoryReport,
                          reports,
                          clearStandardReports,
                          downloadTransactionFileForExcel,
                        }) => {
  const [TOP_SALES_PER_PRODUCT, TOP_SALES_PER_CATEGORY] = ['TopSalesPerProduct', 'TopSalesPerCategory'];
  const reportName = isCategoryReport ? TOP_SALES_PER_CATEGORY : TOP_SALES_PER_PRODUCT;
  const machinesOptions = machines.map(machine => ({ label: machine.Name, value: machine.Id }));
  const reportsFetched = reports && reports?.length > 0;
  const [formValues, setFormValues] = useState(null);
  const printRef = React.useRef();
  const classes = useStyles();
  const topSalesCharts = {
    ...SALES_VAT,
    labelFields: [isCategoryReport ? CATEGORY_COLUMN : PRODUCT_COLUMN, NUMBER_OF_SOLD_GOODS_COLUMN],
  };
  const { formatMessage } = useIntl();
  const MAX_ITEMS = 10;
  
  React.useEffect(() => {
    clearStandardReports();
  }, [clearStandardReports, isCategoryReport]);
  
  const maxItemsReached = (reports) => {
    return reports.length >= MAX_ITEMS;
  };
  
  const filterAndSortReports = (reports) => {
    return reports
      .filter(item => item[CATEGORY_COLUMN] !== TOTAL_LABEL && item[PRODUCT_COLUMN] !== TOTAL_LABEL)
      .sort((a, b) => parseFloat(b[SALES_INCL_VAT]) - parseFloat(a[SALES_INCL_VAT]));
  };
  
  const getFilteredAndSortedReports = (reports, maxItems) => {
    return maxItemsReached(reports) ? filterAndSortReports(reports).slice(0, maxItems) : filterAndSortReports(reports);
  };
  
  const sortByHeaders = (a, b) => {
    return TOP_SALES_HEADERS.indexOf(a) - TOP_SALES_HEADERS.indexOf(b);
  };
  
  const getTopSalesHeaders = (reports) => {
    return Object.keys(reports).filter(item => TOP_SALES_HEADERS.includes(item)).sort(sortByHeaders).map(header => ({
      headerName: getHeaderTranslationForStandardReports(formatMessage, header),
      field: header,
      headerAlign: 'center',
      width: 240,
      valueGetter: (params) => isNumber(params.value) ? Number(params.value) : params.value,
      renderCell: (params) => <DataGridCell header={header} value={params.value} />,
    }));
  };
  
  const getTopSalesRows = (reports) => {
    return reports.map((report, index) => ({ ...report, id: index }));
  };
  
  return (
    <CommonContainer ref={printRef} width={'auto'} hAlign="center" className={classes.pageWrapper}>
      <h1 className={classes.topSalesHeader}>
        {isCategoryReport ? <FormattedMessage
            id="menu.main.reports.top.category-sales"
            defaultMessage="Top sales per category"
          />
          : <FormattedMessage
            id="menu.main.reports.top.product-sales"
            defaultMessage="Top sales per product"
          />}
      </h1>
  
      <TopSalesReportForm
        reportName={reportName}
        machinesOptions={machinesOptions}
        setFormValues={setFormValues}
      />
  
      {reportsFetched && <div id={'chartWrapperID'} className={classes.chartWrapper}>
        <Chart chartData={{
          ...topSalesCharts,
          values: getValues(topSalesCharts.valueFields, getFilteredAndSortedReports(reports, MAX_ITEMS)),
          labels: getLabels(topSalesCharts.labelFields, getFilteredAndSortedReports(reports, MAX_ITEMS)),
        }} />
      </div>}
  
      {reportsFetched && <RegularDataGrid
        columns={getTopSalesHeaders(reports[0])}
        rows={getTopSalesRows(reports)}
        components={{
          Toolbar: () => (
            <DownloadToolbarWithProgress
              reportName={reportName}
              formValues={formValues}
              machinesOptions={machinesOptions}
              downloadTransactionFileForExcel={downloadTransactionFileForExcel}
            />
          ),
        }}
        componentProps={{ printRef: printRef }}
      />}
  
      {(reports && reports.length === 0) && <>
        <CommonDivider />
        <NoDataAvailable />
      </>}

    </CommonContainer>
  );
};

TopSalesReport.propTypes = {
  machines: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  reports: PropTypes.array,
  isCategoryReport: PropTypes.bool,
  clearStandardReports: PropTypes.func,
  downloadTransactionFileForExcel: PropTypes.func,
};

const decorate = compose(
  connect(
    store => ({
      machines: store.user.machines,
      reports: store.report.standard,
    }), dispatch => bindActionCreators({
      clearStandardReports,
      downloadTransactionFileForExcel,
    }, dispatch),
  ),
);

export default decorate(TopSalesReport);
